import {ApolloCache, useMutation} from '@apollo/client'
import {ReferenceManyToManyField} from '@flinkit/ra-relationships'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
} from '@mui/material'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import {makeStyles} from '@mui/styles'
import gql from 'graphql-tag'
import {difference, uniq} from 'lodash-es'
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  FilterLiveSearch,
  FunctionField,
  ReferenceField,
  ReferenceManyField,
  Show,
  SingleFieldList,
  TextField,
  useGetIdentity,
  useGetManyReference,
  useListContext,
  useRedirect,
  useRefresh,
  useTranslate,
} from 'react-admin'
import {useLocation} from 'react-router-dom'

import Accordion from '../../components/Accordion'
import AccordionWrapper from '../../components/AccordionWrapper'
import {ChevronDownIcon, CloseFilledIcon, WarningIcon} from '../../components/icons'
import {CHANNELS_QUERY} from '../../components/SidebarMenu'
import useIsDesktop from '../../hooks/useIsDesktop'
import useSessionMerchantUser from '../../hooks/useSessionMerchantUser'
import {useUserDisplayName} from '../../hooks/useUserDisplayName'
import ExtendedFilterLiveSearch from '../../resources/components/ExtendedFilterLiveSearch'
import ExtendedList from '../../resources/components/ExtendedList'
import ExtendedPagination from '../../resources/components/ExtendedPagination'
import ImportButton from '../../resources/components/ImportButton'
import {AppTheme} from '../../theme'
import {ChannelMerchantUsers, Channels, MutationRoot} from '../../types/graphqlSchema'

const ChannelsAndMerchantUsersManagementShow = () => {
  const {identity: {merchantId} = {}} = useGetIdentity()
  const styles = useStyles({})
  if (!merchantId) return null
  return (
    <Show
      actions={false}
      className={styles.channelsAndMerchantUsersManagementShowRoot}
      id={merchantId}
      resource="merchants"
    >
      <ReferenceManyField
        perPage={false as any}
        reference="channels"
        sort={{field: 'name', order: 'ASC'}}
        target="merchantId"
      >
        <ChannelList />
      </ReferenceManyField>
    </Show>
  )
}

const ChannelList = () => (
  <ExtendedList
    actionButtons={<ChannelMerchantUserImportButton />}
    filters={[
      <ExtendedFilterLiveSearch
        alwaysOn
        key="liveSearch"
        slotProps={{inputLabel: {sx: {paddingTop: '5px'}}}}
        source="name@_ilike,whatsappAccount#phoneNumber@_ilike"
      />,
    ]}
    pagination={<ExtendedPagination rowsPerPageOptions={[]} />}
    perPage={25}
    shouldAlignActionButtonsWithFilterForm
    title={false}
  >
    <ChannelAccordionsList />
  </ExtendedList>
)

const ChannelAccordionsList = () => {
  const translate = useTranslate()
  const {data: channels = []} = useListContext()
  const styles = useStyles({})
  return (
    <AccordionWrapper>{
      channels.map(channel => (
        <Accordion
          TransitionProps={{unmountOnExit: true}}
          elevation={0}
          key={channel.id}
          name={channel.id}
        >
          <AccordionSummary
            className={styles.channelAccordionSummary}
            expandIcon={<ChevronDownIcon />}
          >
            <ReferenceField
              link={false}
              record={channel}
              reference="whatsapp_accounts"
              source="whatsappAccountId"
            >
              <div className={styles.channelName}>
                <TextField source="phoneNumber" variant="subtitle2" />
                <Typography component="span" variant="subtitle1">
                  {" "}({channel.name})
                </Typography>
              </div>
            </ReferenceField>
          </AccordionSummary>
          <AccordionDetails>
            <Card key={channel.id}>
              <CardContent>
                <Typography>
                  {translate('pages.companySettings.tabs.channels.admins')}
                </Typography>
                <ChannelMerchantUsersList
                  channel={channel}
                  isChannelMerchantUsersAdminList
                />
                <Typography>
                  {translate('pages.companySettings.tabs.channels.users')}
                </Typography>
                <ChannelMerchantUsersList channel={channel} />
              </CardContent>
            </Card>
          </AccordionDetails>
        </Accordion>
      ))
    }</AccordionWrapper>
  )
}

const ChannelMerchantUsersList: FC<ChannelMerchantUsersListProps> = ({
  channel, isChannelMerchantUsersAdminList = false,
}) => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const updateChannelsQueryCache = useUpdateChannelsQueryCache()
  const [isPopperOpen, setIsPopperOpen] = useState(false)
  const styles = useStyles({isPopperOpen})
  const anchorElementRef = useRef<HTMLButtonElement>(null)
  const [deleteChannelMerchantUsers] = useMutation<
    MutationRoot['delete_channel_merchant_users']
  >(
    DELETE_ONE_CHANNEL_MERCHANT_USER,
    {
      onCompleted: () => refresh(),
      update: (
        cache,
        // @ts-ignore: delete_channel_merchant_users doesn't exist
        {data: {delete_channel_merchant_users: {returning: channelMerchantUsers}}}
      ) => updateChannelsQueryCache(
        cache, {
          channelId: channel.id,
          channelMerchantUserMutationType: 'DELETE',
          channelMerchantUsers,
        }
      ),
    }
  )
  const [removeChannelMerchantUserAdminPermission] = useMutation(
    REMOVE_CHANNEL_MERCHANT_USER_ADMIN_PERMISSION, {onCompleted: refresh}
  )
  const userDisplayName = useUserDisplayName()
  return (
    <Box>
      <>
        <Button
          className={styles.channelMerchantUsersListPopperButton}
          endIcon={<ChevronDownIcon color="info"/>}
          onClick={() => setIsPopperOpen(t => !t)}
          ref={anchorElementRef}
          size="small"
          variant="outlined"
        >
          {isChannelMerchantUsersAdminList ?
            translate('pages.companySettings.tabs.channels.addAdmins') :
            translate('pages.companySettings.tabs.channels.addUsers')
          }
        </Button>
        <Popper
          anchorEl={anchorElementRef.current}
          className={styles.popper}
          open={isPopperOpen}
          placement="bottom-start"
        >
          <ClickAwayListener onClickAway={() => setIsPopperOpen(false)}>
            <Paper>
              <ReferenceManyField
                perPage={1000}
                record={channel}
                reference="merchant_users"
                sort={{field: 'firstName', order: 'ASC'}}
                source="merchantId"
                target="merchantId"
              >
                <FilterLiveSearch className={styles.channelMerchantUsersSearchBar} />
                <MerchantUsersList
                  bulkActionButtons={false}
                  channel={channel}
                  isChannelMerchantUsersAdminList={isChannelMerchantUsersAdminList}
                  onApply={() => setIsPopperOpen(false)}
                  onClearAll={() => setIsPopperOpen(false)}
                />
              </ReferenceManyField>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </>
      <ReferenceManyToManyField
        filter={{isAdmin: isChannelMerchantUsersAdminList || undefined}}
        record={channel}
        reference="merchant_users"
        sort={{field: "merchantUserId", order: 'DESC'}}
        through="channel_merchant_users"
        using="channelId,merchantUserId"
      >
        <SingleFieldList className={styles.singleChipList} linkType={false}>
          <FunctionField
            label="Name"
            render={record => (
              <Chip
                deleteIcon={<CloseFilledIcon color="info"/>}
                disabled={isChannelMerchantUsersAdminList && record?.isOwner}
                label={<Typography>{userDisplayName({merchantUser: record})}</Typography>}
                onDelete={() => {
                  isChannelMerchantUsersAdminList ?
                    removeChannelMerchantUserAdminPermission({
                      variables: {channelId: channel.id, merchantUserId: record?.id},
                    }) :
                    deleteChannelMerchantUsers({
                      variables: {channelId: channel.id, merchantUserId: record?.id},
                    })
                }}
                variant="outlined"
              />
            )}
            variant="body1"
          />
        </SingleFieldList>
      </ReferenceManyToManyField>
    </Box>
  )
}

const MerchantUsersList: FC<MerchantUsersListProps> = ({
  channel,
  isChannelMerchantUsersAdminList,
  onApply,
  onClearAll,
}) => {
  const translate = useTranslate()
  const userDisplayName = useUserDisplayName()
  const {data: merchantUsers = []} = useListContext()
  const styles = useStyles({})
  const refresh = useRefresh()
  const updateChannelsQueryCache = useUpdateChannelsQueryCache()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const {data: channelMerchantUsers = []} = useGetManyReference(
    'channel_merchant_users',
    {
      id: channel.id,
      sort: {field: 'merchantUserId', order: 'ASC'},
      target: 'channelId',
    },
  )
  const isChannelMerchantUserAdmin = useCallback(
    (merchantUserId: string) =>
      !!channelMerchantUsers.find(c => c.merchantUserId === merchantUserId)?.isAdmin,
    [channelMerchantUsers],
  )
  const initiallySelectedMerchantUserIds = useMemo(() =>
    merchantUsers.filter(
      mu => isChannelMerchantUsersAdminList ?
        isChannelMerchantUserAdmin(mu.id) :
        channelMerchantUsers.some(c => c.merchantUserId === mu.id)
    ).map(mu => mu.id), [
    channelMerchantUsers,
    isChannelMerchantUserAdmin,
    isChannelMerchantUsersAdminList,
    merchantUsers,
  ])
  const unselectedMerchantUserIds = useMemo(
    () => difference(merchantUsers.map(mu => mu.id), selectedIds),
    [merchantUsers, selectedIds],
  )
  useEffect(() => {
    if (selectedIds.length) return
    setSelectedIds(selectedIds => uniq([
      ...selectedIds,
      ...initiallySelectedMerchantUserIds,
    ]))
  }, [initiallySelectedMerchantUserIds, setSelectedIds, selectedIds])
  const [upsertChannelMerchantUsers] = useMutation<
    MutationRoot['insert_channel_merchant_users']
  >(
    UPSERT_CHANNEL_MERCHANT_USERS,
    {
      update: (
        cache,
        // @ts-ignore: insert_channel_merchant_users don't exist
        {data: {insert_channel_merchant_users: {returning: channelMerchantUsers}}}
      ) => updateChannelsQueryCache(
        cache, {
          channelId: channel.id,
          channelMerchantUserMutationType: 'UPSERT',
          channelMerchantUsers,
        }
      ),
      variables: {
        objects: selectedIds.map(merchantUserId => ({
          channelId: channel.id,
          isAdmin: isChannelMerchantUsersAdminList ||
            isChannelMerchantUserAdmin(merchantUserId),
          merchantUserId,
        })),
      },
    }
  )
  const [removeUnselectedChannelMerchantUsersAdminPermission] = useMutation(
    REMOVE_UNSELECTED_CHANNEL_MERCHANT_USERS_ADMIN_PERMISSION,
    {
      variables: {
        channelId: channel.id,
        unselectedMerchantUserIds,
      },
    }
  )
  const [removeAllChannelMerchantUsersAdminPermission] = useMutation(
    REMOVE_ALL_CHANNEL_MERCHANT_USERS_ADMIN_PERMISSION,
    {variables: {channelId: channel.id}},
  )
  const [deleteAllChannelMerchantUsers] = useMutation<
    MutationRoot['delete_channel_merchant_users']
  >(
    DELETE_ALL_CHANNEL_MERCHANT_USERS,
    {
      onCompleted: () => refresh(),
      update: (
        cache, {
          // @ts-ignore: delete_channel_merchant_users doesn't exist
          data: {delete_channel_merchant_users: {returning: channelMerchantUsers}},
        }) => updateChannelsQueryCache(
        cache, {
          channelId: channel.id,
          channelMerchantUserMutationType: 'DELETE',
          channelMerchantUsers,
        }
      ),
      variables: {channelId: channel.id},
    },
  )
  const [deleteUnselectedChannelMerchantUsers] = useMutation<
    MutationRoot['delete_channel_merchant_users']
  >(
    DELETE_UNSELECTED_CHANNEL_MERCHANT_USERS,
    {
      update: (
        cache,
        // @ts-ignore: delete_channel_merchant_users doesn't exist
        {data: {delete_channel_merchant_users: {returning: channelMerchantUsers}}}
      ) => updateChannelsQueryCache(
        cache, {
          channelId: channel.id,
          channelMerchantUserMutationType: 'DELETE',
          channelMerchantUsers,
        }
      ),
      variables: {
        channelId: channel.id,
        unselectedMerchantUserIds,
      },
    }
  )
  return (
    <>
      <div className={styles.merchantUsersList}>
        <Checkbox
          checked={merchantUsers.every(mu => selectedIds.includes(mu.id))}
          onChange={e => {
            setSelectedIds(e.target.checked ?
              merchantUsers.map(mu => mu.id) :
              initiallySelectedMerchantUserIds
            )
          }}
        />
        {merchantUsers.map(merchantUser => (
          <div key={merchantUser.id}>
            <Checkbox
              checked={selectedIds.some(id => id === merchantUser.id)}
              disabled={
                isChannelMerchantUsersAdminList &&
                merchantUser.isOwner &&
                isChannelMerchantUserAdmin(merchantUser.id)
              }
              onChange={e => {
                if (e.target.checked) {
                  setSelectedIds(ids => [...ids, merchantUser.id])
                }
                else {
                  setSelectedIds(ids => ids.filter(id => id !== merchantUser.id))
                }
              }}
            />
            <Typography component="span">{userDisplayName({merchantUser})}</Typography>
          </div>
        ))}
      </div>
      <Box paddingRight=".5rem" paddingTop="1rem" textAlign="end">
        <Button
          onClick={async () => {
            isChannelMerchantUsersAdminList ?
              await removeAllChannelMerchantUsersAdminPermission() :
              await deleteAllChannelMerchantUsers()
            refresh()
            onClearAll()
          }}
          variant="outlined"
        >
          {translate('pages.companySettings.tabs.channels.removeAll')}
        </Button>
        <Button
          color="primary"
          onClick={async () => {
            await Promise.all([
              upsertChannelMerchantUsers(),
              isChannelMerchantUsersAdminList ?
                removeUnselectedChannelMerchantUsersAdminPermission() :
                deleteUnselectedChannelMerchantUsers(),
            ])
            refresh()
            onApply()
          }}
          variant="contained"
        >
          {translate('pages.companySettings.tabs.channels.apply')}
        </Button>
      </Box>
    </>
  )
}

const ChannelMerchantUserImportButton = () => {
  const styles = useStyles({})
  const translate = useTranslate()
  const isDesktop = useIsDesktop()
  const reorderInvalidRowFields = useCallback((rows: any[]) => rows.map(
    ({emailAddress, failureReason, isAdmin, phoneNumber, ...rest}) => ({
      // eslint-disable-next-line sort-keys, sort-keys-fix/sort-keys-fix
      emailAddress, phoneNumber, isAdmin, ...rest, failureReason,
    })
  ), [])
  const getImportArgsVariablesFromFile = useCallback(({file}) => ({
    csvBase64Content: file.encodedContent,
    mimeType: file.mimeType,
  }), [])
  const labels = useMemo(() => ({
    confirm: filename => translate(
      'resources.channel_merchant_users.import.confirm', {filename},
    ),
    error: e => (
      <Typography>
        <WarningIcon color="error" size="inherit" />
        {e.message}
      </Typography>
    ),
    failureReason: r => translate(
      `resources.channel_merchant_users.importRowFailureReasons.${r}`
    ),
    progressMessage: translate(
      'resources.channel_merchant_users.import.progressDialog.progressMessage'
    ),
    progressTitle: translate(
      'resources.channel_merchant_users.import.progressDialog.title',
    ),
    success: count => translate(
      'resources.channel_merchant_users.import.success',
      {importedChannelMerchantUsersCount: count}
    ),
  }), [translate])
  return (
    <ImportButton
      className={styles.channelMerchantUserImportButton}
      mutation={CHANNEL_MERCHANT_USERS_IMPORT_MUTATION}
      buildVariables={getImportArgsVariablesFromFile}
      parseResult={data => {
        const r = data.importChannelMerchantUsers
        return {
          failureReasons: r.rowFailureReasons,
          invalidRows: r.invalidRows,
          invalidRowsCount: r.invalidRowsCount,
          successCount: r.importedChannelMerchantUsersCount,
        }
      }}
      exportInvalidRows={{
        fileName: 'channel_merchant_users',
        transform: reorderInvalidRowFields,
      }}
      labels={labels}
    >
      {isDesktop && translate('resources.channel_merchant_users.import.button')}
    </ImportButton>
  )
}

const useUpdateChannelsQueryCache = () => {
  const {merchantUser: {id: merchantUserId} = {}} = useSessionMerchantUser()
  const {pathname} = useLocation()
  const redirect = useRedirect()
  return useCallback<(cache: ApolloCache<unknown>,
    options: {
      channelId: string,
      channelMerchantUserMutationType: 'DELETE' | 'UPSERT',
      channelMerchantUsers: ChannelMerchantUsers[],
  }) => void>((
      cache, {channelId, channelMerchantUserMutationType, channelMerchantUsers}
    ) => {
      const {channels}: any = cache.readQuery({
        query: CHANNELS_QUERY,
        variables: {merchantUserId},
      })
      const channelMerchantUser = channelMerchantUsers.find(
        cmu => cmu.channelId === channelId && cmu.merchantUserId === merchantUserId
      )
      if (!channelMerchantUser) return
      if (channelMerchantUserMutationType === 'UPSERT') {
        cache.writeQuery({
          data: {
            channels: channels.some(c => c.id === channelId) ?
              channels :
              [channelMerchantUser.channel, ...channels],
          },
          query: CHANNELS_QUERY,
          variables: {merchantUserId},
        })
      }
      if (channelMerchantUserMutationType === 'DELETE') {
        cache.writeQuery({
          data: {
            channels: channels.filter(c => c.id !== channelId),
          },
          query: CHANNELS_QUERY,
          variables: {merchantUserId},
        })
        // We don't want the user to stay on a channel inbox he is no longer member of.
        if (pathname.startsWith(`/inbox/${channelId}`)) redirect('/inbox')
      }
    }, [merchantUserId, pathname, redirect])
}

const useStyles = makeStyles<AppTheme, MakeStylesOptions>(theme => ({
  channelAccordionSummary: {
    '& .MuiAccordionSummary-content': {
      width: `calc(100% - ${theme.remSpacing(12)})`,
    },
  },
  channelMerchantUserImportButton: {
    height: theme.remSpacing(7.5),
  },
  channelMerchantUsersListPopperButton: ({isPopperOpen}) => ({
    '& svg': {
      rotate: isPopperOpen ? '180deg' : '0deg',
      transitionDuration: '.2s',
      transitionProperty: 'rotate',
    },
    borderRadius: theme.remSpacing(2.5),
    height: theme.remSpacing(4),
  }),
  channelMerchantUsersSearchBar: {
    margin: `${theme.remSpacing(1)} ${theme.remSpacing(2)}`,
    width: 'max-content',
  },
  channelName: {
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as any,
  },
  channelsAndMerchantUsersManagementShowRoot: {
    '& .MuiChip-root': {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.info.main,
      color: theme.palette.primary.main,
      margin: theme.remSpacing(1),
      marginRight: theme.remSpacing(1),
    },
    minWidth: '100%',
    padding: 0,
  },
  merchantUsersList: {
    '& > :hover': {
      background: theme.palette.background.main,
    },
    marginLeft: theme.remSpacing(.5),
    maxHeight: theme.remSpacing(40),
    overflow: 'auto',
  },
  popper: {
    '& .MuiToolbar-root': {
      marginLeft: theme.remSpacing(2),
      marginRight: theme.remSpacing(0),
    },
    '& > .MuiPaper-root': {
      boxShadow: '4px 4px 14px rgba(0, 0, 0, 0.15)',
      paddingBottom: theme.remSpacing(1),
    },
    [theme.breakpoints.down('lg')]: {
      maxWidth: '80%',
    },
    zIndex: 10000,
  },
  singleChipList: {
    display: 'inline-block',
    marginBottom: theme.remSpacing(2),
    marginTop: theme.remSpacing(1),
  },
}))

const DELETE_ONE_CHANNEL_MERCHANT_USER = gql`
  mutation($channelId: uuid!, $merchantUserId: uuid!){
    delete_channel_merchant_users(
      where: {channelId: {_eq: $channelId}, merchantUserId: {_eq: $merchantUserId}}
    ){returning{channelId merchantUserId}}
  }
`

const DELETE_ALL_CHANNEL_MERCHANT_USERS = gql`
  mutation($channelId: uuid!){
    delete_channel_merchant_users(where: {channelId: {_eq: $channelId}}){
      returning{channelId merchantUserId}
    }
  }
`

const DELETE_UNSELECTED_CHANNEL_MERCHANT_USERS = gql`
  mutation($channelId: uuid!, $unselectedMerchantUserIds: [uuid!]){
    delete_channel_merchant_users(
      where: {
        channelId: {_eq: $channelId},
        merchantUserId: {_in: $unselectedMerchantUserIds}
      }
    ){returning{channelId merchantUserId}}
  }
`

const REMOVE_CHANNEL_MERCHANT_USER_ADMIN_PERMISSION = gql`
  mutation ($channelId: uuid!, $merchantUserId: uuid!){
    update_channel_merchant_users(
      where: {channelId: {_eq: $channelId}, merchantUserId: {_eq: $merchantUserId}},
      _set: {isAdmin: false}
    ){returning{channelId merchantUserId}}
  }
`

const REMOVE_ALL_CHANNEL_MERCHANT_USERS_ADMIN_PERMISSION = gql`
  mutation ($channelId: uuid!){
    update_channel_merchant_users(
      where: {channelId: {_eq: $channelId}, merchantUser: {isOwner: {_eq: false}}},
      _set: {isAdmin: false}
    ){returning{channelId merchantUserId}}
  }
`

const REMOVE_UNSELECTED_CHANNEL_MERCHANT_USERS_ADMIN_PERMISSION = gql`
  mutation ($channelId: uuid!, $unselectedMerchantUserIds: [uuid!]){
    update_channel_merchant_users(
      where: {
        channelId: {_eq: $channelId}, merchantUserId: {_in: $unselectedMerchantUserIds}
      },
      _set: {isAdmin: false}
    ){returning{channelId merchantUserId}}
  }
`

const UPSERT_CHANNEL_MERCHANT_USERS = gql`
  mutation($objects: [channel_merchant_users_insert_input!]!){
    insert_channel_merchant_users(
      objects: $objects,
      on_conflict: {constraint: channel_merchant_users_pkey, update_columns: isAdmin}
    ){returning{channelId merchantUserId, channel{id name whatsappAccount{phoneNumber}}}}
  }
`

const CHANNEL_MERCHANT_USERS_IMPORT_MUTATION = gql`
  mutation ($csvBase64Content: String!, $mimeType: String!){
    importChannelMerchantUsers(
      csvBase64Content: $csvBase64Content
      mimeType: $mimeType
    ){
      importedChannelMerchantUsersCount
      invalidRows
      invalidRowsCount
      rowFailureReasons
    }
  }
`

interface ChannelMerchantUsersListProps {
  channel: Channels,
  isChannelMerchantUsersAdminList?: boolean
}

interface MerchantUsersListProps {
  [props: string]: any
  channel: Channels,
  isChannelMerchantUsersAdminList: boolean,
  onApply: VoidFunction,
  onClearAll: VoidFunction,
}

interface MakeStylesOptions {
  isPopperOpen?: boolean
}

export {ChannelAccordionsList}
export default ChannelsAndMerchantUsersManagementShow
